import React from 'react'
import { useSelector } from 'react-redux'
import { Card } from '@mytours/components'
import { BundleOffer, RootStoreState } from 'mytours-read-common'
import classnames from 'classnames'

import Page from '../../common/components/Page'
import BundleUnlockButton from './BundleUnlockButton'
import strings from '../../../strings'

/**
 * The gate for a collection whose only lock comes from a bundle.
 *
 * Deliberately not LockApp: there is no product on the collection, so there is no collection code to
 * type and a code field here could never succeed — it would redeem against linked_type=Collection and
 * always come back invalid. The bundles that sell the collection are the whole way in.
 *
 * Borrows LockApp's page shell so a visitor sees the same locked-content furniture either way.
 */
const BundleOnlyLock: React.FC<{
	bundles: BundleOffer[]
	accountId?: number
	backgroundUrl?: string
}> = ({ bundles, accountId, backgroundUrl }) => {
	const bundleProducts = useSelector((state: RootStoreState) => state.bundles.bundleProducts)
	const bundleFetchFailed = useSelector((state: RootStoreState) => state.bundles.bundleFetchFailed)
	// The lock came with the collection, so it holds either way; this only says whether we could find out
	// what the bundles are called and what they cost.
	const unresolved = bundles.filter(bundle => bundle.id !== undefined && !bundleProducts[bundle.id])
	const cannotReach = unresolved.some(bundle => bundleFetchFailed?.[String(bundle.id)])

	return (
		<Page
			contentProps={{
				className: classnames('lock-app'),
				style: backgroundUrl
					? {
							background: `url(${backgroundUrl}) no-repeat center center / cover`,
							'--content-background-image': 'url()',
						}
					: undefined,
			}}
			dataCy="page-bundle-only-lock"
		>
			<Card variant="ios" className="center-block">
				<i className="lock-icon fal lock-alt" />
				<h1>{strings.bundle_unlock_title}</h1>
				<p>{strings.bundle_unlock_description}</p>
				<BundleUnlockButton bundles={bundles} accountId={accountId} />
				{cannotReach && <p data-cy="bundle-unresolved">{strings.connection_error_message}</p>}
			</Card>
		</Page>
	)
}

export default BundleOnlyLock
